import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import {
  X, MapPin, Shield, Clock, Users, TrendingUp, Signal,
  CheckCircle, XCircle, AlertTriangle, Minus, ChevronDown, Bell, BellOff, Lock, Crown, Layers,
  Pencil, Trash2, Loader2, UserCheck, Users2, HandCoins, PiggyBank, Send
} from "lucide-react";
import { toast } from "sonner";
import { getStatusDisplay } from "@/utils/statusDisplay";
import { HelpButton } from "@/components/HelpOverlay";
import { ATM_DETAIL_HELP } from "@/utils/helpContent";
import { formatJamaicaTime } from "@/utils/timeFormat";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import BankSelect from "@/components/BankSelect";

const STATUS_COLORS = {
  working: "#00FF94",
  out_of_service: "#FF3B30",
  out_of_cash: "#FFCC00",
  unknown: "#71717A",
};

const STATUS_LABELS = {
  working: "Working",
  out_of_service: "Out of Service",
  out_of_cash: "Out of Cash",
  unknown: "Unknown",
};

const LINE_LABELS = {
  no_line: "No Line",
  short_line: "Short Line",
  long_line: "Long Line",
  unknown: "Unknown",
};

export default function ATMDetailSheet({ atm, onClose, user, onReportSubmitted, myCircles = [] }) {
  const [reports, setReports] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Post-submission "fade-out" window so the map fly-in + impact pulse
  // are visible underneath the sheet. Triggered on successful report,
  // clears after 1.6s along with sheet auto-close. Timer id stashed in a
  // ref so we can cancel it if the user manually dismisses the sheet
  // mid-fade (prevents React "state update on unmounted component" warn).
  const [sheetFading, setSheetFading] = useState(false);
  const fadeTimerRef = useRef(null);
  useEffect(() => () => {
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
  }, []);
  const [detailedAtm, setDetailedAtm] = useState(atm);
  const [cooldown, setCooldown] = useState({ can_report: true, reports_remaining: 2, next_available: null });
  const [isWatching, setIsWatching] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);
  // Phase A: per-capability submission. Default `withdraw` matches Jamaican
  // usage (~95% of reports historically). When `deposit` is selected we hide
  // the "No Cash" status button (a deposit can't be out of cash) — backend
  // also rejects that combo as a safety net.
  const [transactionType, setTransactionType] = useState("withdraw");
  const [comment, setComment] = useState("");
  const [switchingSibling, setSwitchingSibling] = useState(null);
  // Admin edit/delete state (only touched when `user.is_admin`). Kept local to
  // the sheet so the Map page stays unaware of admin mutation concerns.
  const [adminEditOpen, setAdminEditOpen] = useState(false);
  const [adminEditForm, setAdminEditForm] = useState({
    bank_name: "", address_street: "", address_area: "", address_parish: "",
    unit_label: "", latitude: "", longitude: "",
  });
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminDeleting, setAdminDeleting] = useState(false);
  // Trusted Circles sticky-share toggle state. Loaded on mount alongside the
  // watch-status check; stays in sync when the user taps the toggle.
  const [trustedShare, setTrustedShare] = useState(false);
  const [trustedLoading, setTrustedLoading] = useState(false);

  // ── Drag-to-dismiss for the bottom sheet handle ──────────────────────────
  // The little horizontal bar at the top is the universal mobile pattern for
  // "drag me down to close". We track pointer events to translate the sheet
  // while dragging, then either snap back or close based on distance + velocity.
  const sheetRef = useRef(null);
  const submitReportRef = useRef(null);
  const dragStateRef = useRef({ startY: 0, lastY: 0, lastT: 0, velocity: 0, dragging: false, pointerId: null });
  const [dragOffset, setDragOffset] = useState(0);
  // Two-state snap sheet (Feb 2026). Default "peek" leaves ~58vh of map
  // visible so users can still see the ATM's alert-radius overlay + nearby
  // pins. Expands to full 75vh when the user drags up, taps the peek CTA,
  // or opens Watch/Share/Report actions that live below the fold.
  const [sheetMode, setSheetMode] = useState("peek");
  const expandAndScrollToReport = useCallback(() => {
    setSheetMode("expanded");
    // Wait for the max-height transition (~350ms) before scrolling — else
    // scrollIntoView fires while the sheet is still collapsed and undershoots.
    setTimeout(() => {
      submitReportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 380);
  }, []);

  const handleDragStart = useCallback((e) => {
    const y = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    dragStateRef.current = {
      startY: y, lastY: y, lastT: performance.now(),
      velocity: 0, dragging: true, pointerId: e.pointerId,
    };
    // Capture so we keep getting move events even if the pointer leaves the handle.
    if (e.currentTarget.setPointerCapture && e.pointerId != null) {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    }
  }, []);

  const handleDragMove = useCallback((e) => {
    const s = dragStateRef.current;
    if (!s.dragging) return;
    const y = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    const dy = Math.max(0, y - s.startY);  // only allow downward
    const now = performance.now();
    const dt = now - s.lastT;
    if (dt > 0) s.velocity = (y - s.lastY) / dt;  // px/ms
    s.lastY = y; s.lastT = now;
    setDragOffset(dy);
  }, []);

  const handleDragEnd = useCallback(() => {
    const s = dragStateRef.current;
    if (!s.dragging) return;
    const distance = Math.max(0, s.lastY - s.startY);
    const fastSwipe = s.velocity > 0.5;          // >500 px/s downward
    const farEnough = distance > 80;              // 80px threshold
    s.dragging = false;
    // Tap (barely any movement) → toggle peek ↔ expanded. Gives users a
    // discoverable one-tap way to expand without needing to find the CTA.
    if (distance < 6 && Math.abs(s.velocity) < 0.3) {
      setSheetMode((m) => (m === "peek" ? "expanded" : "peek"));
      setDragOffset(0);
      return;
    }
    if (fastSwipe || farEnough) {
      // Animate offscreen then close. Sheet max-height is 75vh ≈ ample.
      setDragOffset(window.innerHeight);
      setTimeout(() => onClose?.(), 180);
    } else {
      setDragOffset(0);
    }
  }, [onClose]);

  // The currently-displayed ATM id. After a sibling switch this differs from the
  // original `atm` prop; all in-component fetches must target this id, not the prop.
  const currentId = detailedAtm?.id || atm.id;

  // When the parent hands us a NEW ATM (user clicked a different marker),
  // React reuses this component instance — internal state does NOT reset
  // automatically. Prior to Feb 2026 this caused: `detailedAtm` stayed set
  // to the previously-opened ATM, `currentId` (derived from it) pointed at
  // the OLD id, and `fetchDetails()` called with no override refetched the
  // OLD ATM's data → sheet kept showing the first-clicked ATM.
  //
  // Fix: on every prop-atm-id change (a) instantly swap `detailedAtm` to
  // the incoming prop so the sheet re-renders with the new ATM's basic
  // info without a flash of stale content, (b) call `fetchDetails(atm.id)`
  // with an explicit override so the enrichment fetch targets the correct
  // id, and (c) reset the transient sheet UX state (peek mode, half-filled
  // report form, prior reports list) — a marker click is a fresh opening,
  // not a continuation of the previous one.
  useEffect(() => {
    setDetailedAtm(atm);
    setReports([]);
    setSheetMode("peek");
    setSelectedStatus(null);
    setSelectedLine(null);
    setComment("");
    setSwitchingSibling(null);
    fetchDetails(atm.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atm.id]);

  // Trusted-sharing and watch status are per-ATM, so they must re-fetch
  // whenever the *currently displayed* ATM changes — including sibling
  // switches inside a clustered branch. Keying on `atm.id` only would leave
  // the toggle stuck on the originally-opened sibling's state. Pre-Phase-A
  // version had this bug; cluster siblings appeared to "share" the toggle.
  useEffect(() => {
    if (!user) return;
    checkWatchStatus();
    checkTrustedStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, user?.user_id]);

  const checkTrustedStatus = async () => {
    try {
      const res = await axios.get(`${API}/trusted/subscriptions`, { withCredentials: true });
      const active = (res.data || []).some((s) => s.atm_id === currentId);
      setTrustedShare(active);
    } catch { /* silent */ }
  };

  const toggleTrustedShare = async () => {
    if (!user) { toast.error("Sign in to use trusted circles"); return; }
    setTrustedLoading(true);
    try {
      if (trustedShare) {
        await axios.delete(`${API}/trusted/subscriptions/${currentId}`, { withCredentials: true });
        setTrustedShare(false);
        toast.success("Trusted sharing disabled for this ATM");
      } else {
        await axios.post(`${API}/trusted/subscriptions/${currentId}`, {}, { withCredentials: true });
        setTrustedShare(true);
        toast.success("Trusted sharing enabled — your reports will notify nearby circle members");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle trusted sharing");
    } finally {
      setTrustedLoading(false);
    }
  };

  const checkWatchStatus = async () => {
    try {
      const res = await axios.get(`${API}/notifications/status`, { withCredentials: true });
      setIsWatching(res.data.watched_atms?.includes(currentId) || false);
    } catch {}
  };

  const toggleWatch = async () => {
    if (!user) {
      toast.error("Sign in to watch ATMs");
      return;
    }
    try {
      if (isWatching) {
        await axios.post(`${API}/notifications/unwatch-atm/${currentId}`, {}, { withCredentials: true });
        setIsWatching(false);
        toast.success("Stopped watching this ATM");
      } else {
        await axios.post(`${API}/notifications/watch-atm/${currentId}`, {}, { withCredentials: true });
        setIsWatching(true);
        toast.success("Watching this ATM! Enable push notifications in Profile to get alerts.");
      }
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(err.response.data.detail || "Watch limit reached. Upgrade to Pro for more.");
      } else {
        toast.error("Failed to update watch status");
      }
    }
  };

  const fetchDetails = async (idOverride) => {
    const targetId = idOverride || currentId;
    try {
      const requests = [
        axios.get(`${API}/atms/${targetId}`),
        axios.get(`${API}/reports/${targetId}`),
      ];
      if (user) {
        requests.push(axios.get(`${API}/reports/${targetId}/user-cooldown`, { withCredentials: true }));
      }
      const results = await Promise.all(requests);
      setDetailedAtm(results[0].data);
      setReports(results[1].data);
      if (user && results[2]) {
        setCooldown(results[2].data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const submitReport = async () => {
    if (!user) {
      toast.error("Sign in to submit reports");
      return;
    }
    if (!selectedStatus) {
      toast.error("Please select an ATM status");
      return;
    }
    if (!selectedLine) {
      toast.error("Please select a line length");
      return;
    }
    if (!cooldown.can_report) {
      const nextTime = cooldown.next_available ? formatJamaicaTime(cooldown.next_available) : "later";
      toast.error(`Rate limit reached. You can report again at ${nextTime}`);
      return;
    }
    // Auto-flip the status if the user picked "out_of_cash" then switched to deposit.
    // The backend rejects (deposit, out_of_cash) so block the bad combo at the source.
    if (transactionType === "deposit" && selectedStatus === "out_of_cash") {
      setSelectedStatus(null);
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/reports`, {
        atm_id: currentId,
        status: selectedStatus,
        line_status: selectedLine,
        transaction_type: transactionType,
        comment: comment.trim() ? comment.trim() : undefined,
      }, { withCredentials: true });
      toast.success("Report submitted!");
      if (res.data.new_badges?.length > 0) {
        res.data.new_badges.forEach(b => {
          toast.success(`Badge earned: ${b}!`, { duration: 5000 });
        });
      }
      setSelectedStatus(null);
      setSelectedLine(null);
      setComment("");
      await fetchDetails(currentId);
      onReportSubmitted?.();
      // Fly-in animation moment (Mar 2026 v2.0). Broadcast the event so the
      // map can animate a small teardrop from the user's GPS to this ATM
      // and briefly fade our sheet so the animation is visible underneath.
      try {
        window.dispatchEvent(new CustomEvent("taladraw:report-submitted", {
          detail: {
            atmId: currentId,
            atmLng: a.longitude,
            atmLat: a.latitude,
          },
        }));
      } catch (_e) { /* older browsers without CustomEvent */ }
      // Fade the sheet so users see the map animation. After ~1.6s (matches
      // fly-in 800ms + impact-pulse 800ms) auto-close so the moment feels
      // like a completed gesture. Users can still dismiss the sheet
      // manually via close-X mid-animation.
      setSheetFading(true);
      fadeTimerRef.current = setTimeout(() => {
        fadeTimerRef.current = null;
        setSheetFading(false);
        onClose?.();
      }, 1600);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error(err.response.data.detail || "Too many reports. Wait 60 minutes.");
        try {
          const cd = await axios.get(`${API}/reports/${currentId}/user-cooldown`, { withCredentials: true });
          setCooldown(cd.data);
        } catch {}
      } else {
        toast.error(err.response?.data?.detail || "Failed to submit report");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const switchToSibling = async (siblingId) => {
    if (siblingId === detailedAtm.id) return;
    setSwitchingSibling(siblingId);
    try {
      const res = await axios.get(`${API}/atms/${siblingId}`);
      setDetailedAtm(res.data);
      const reportsRes = await axios.get(`${API}/reports/${siblingId}`);
      setReports(reportsRes.data);
      if (user) {
        const cd = await axios.get(`${API}/reports/${siblingId}/user-cooldown`, { withCredentials: true });
        setCooldown(cd.data);
      }
      setSelectedStatus(null);
      setSelectedLine(null);
      setComment("");
    } catch (err) {
      console.error("Sibling switch failed", err);
    } finally {
      setSwitchingSibling(null);
    }
  };

  const openAdminEdit = () => {
    // Prefill the form from the currently-displayed ATM so the admin can
    // make small tweaks without retyping everything.
    setAdminEditForm({
      bank_name: detailedAtm.bank_name || "",
      address_street: detailedAtm.address_street || "",
      address_area: detailedAtm.address_area || "",
      address_parish: detailedAtm.address_parish || "",
      unit_label: detailedAtm.unit_label || "",
      latitude: detailedAtm.latitude != null ? String(detailedAtm.latitude) : "",
      longitude: detailedAtm.longitude != null ? String(detailedAtm.longitude) : "",
    });
    setAdminEditOpen(true);
  };

  const submitAdminEdit = async () => {
    setAdminSaving(true);
    try {
      await axios.put(`${API}/admin/atms/${currentId}`, {
        bank_name: adminEditForm.bank_name || undefined,
        address_street: adminEditForm.address_street || undefined,
        address_area: adminEditForm.address_area || undefined,
        address_parish: adminEditForm.address_parish || undefined,
        // `unit_label` is allowed to be an empty string (to clear it), so
        // don't collapse to undefined.
        unit_label: adminEditForm.unit_label,
        latitude: adminEditForm.latitude ? parseFloat(adminEditForm.latitude) : undefined,
        longitude: adminEditForm.longitude ? parseFloat(adminEditForm.longitude) : undefined,
      }, { withCredentials: true });
      toast.success("ATM updated");
      setAdminEditOpen(false);
      await fetchDetails(currentId);
      onReportSubmitted?.(); // refresh map markers
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update ATM");
    } finally {
      setAdminSaving(false);
    }
  };

  const handleAdminDelete = async () => {
    // Native confirm keeps parity with the existing admin dashboard delete
    // flow and avoids pulling in an AlertDialog for a one-off.
    const label = detailedAtm.bank_name + (detailedAtm.unit_label ? ` (${detailedAtm.unit_label})` : "");
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    setAdminDeleting(true);
    try {
      await axios.delete(`${API}/admin/atms/${currentId}`, { withCredentials: true });
      toast.success("ATM deleted");
      onReportSubmitted?.(); // refresh map markers
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete ATM");
      setAdminDeleting(false);
    }
  };

  const a = detailedAtm;
  const confidenceClass = a.confidence === "high" ? "confidence-high" : a.confidence === "medium" ? "confidence-medium" : "confidence-low";

  return (
    <div
      ref={sheetRef}
      className={`bottom-sheet glass-heavy mode-${sheetMode} ${sheetFading ? "sheet-fading" : ""}`}
      data-testid="atm-detail-sheet"
      data-sheet-mode={sheetMode}
      style={{
        transform: dragOffset ? `translateY(${dragOffset}px)` : undefined,
        transition: dragStateRef.current.dragging ? "none" : "transform 0.22s cubic-bezier(0.32, 0.72, 0, 1)",
      }}
    >
      <div
        className="bottom-sheet-handle-hit"
        role="button"
        aria-label="Drag down to close"
        data-testid="atm-detail-handle"
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
      >
        <div className="bottom-sheet-handle" />
      </div>

      {/* Sibling ATM switcher — shown when this location has multiple machines */}
      {a.group_size > 1 && (
        <div className="px-6 pb-3" data-testid="sibling-switcher">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#00FF94] mb-2">
            <Layers className="w-3 h-3" strokeWidth={2} />
            {a.group_size} ATMs at this branch — pick the one you're at
          </div>
          <div className="tab-scroll gap-2 pb-1">
            {/* Current ATM pill */}
            <SiblingPill
              active
              index={a.group_index}
              label={a.unit_label}
              status={a.status}
              loading={false}
            />
            {(a.group_siblings || []).map((sib) => (
              <SiblingPill
                key={sib.id}
                active={false}
                index={null}
                label={sib.unit_label}
                status={sib.status}
                loading={switchingSibling === sib.id}
                onClick={() => switchToSibling(sib.id)}
                testId={`sibling-${sib.id}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Header — v2.0 hero layout (Mar 2026). Big status pill leads, then
          bank identity, then address. Secondary actions (watch/share/admin)
          are grouped into a compact pill row. Close X is floated top-right
          for one-handed reach. */}
      <div className="px-6 pb-4" data-testid="atm-detail-hero">
        <div className="flex items-start justify-between gap-3 mb-3">
          {/* Big status pill — the ONE thing users care about most */}
          <div className="flex items-center gap-2 flex-wrap">
            {(() => {
              const caps = a.capabilities || {};
              const withdrawCap = caps.withdraw;
              const depositCap = caps.deposit;
              const primary = withdrawCap
                ? { status: withdrawCap.status, last_report_time: withdrawCap.last_report_time }
                : a;
              const sd = getStatusDisplay(primary);
              const primaryStatus = primary?.status || "unknown";
              const showDepositChip = depositCap && depositCap.status !== withdrawCap?.status;
              const depLabel = STATUS_LABELS[depositCap?.status] || "Unknown";
              return (
                <>
                  <span
                    className={`status-badge ${primaryStatus} text-sm px-3 py-1.5`}
                    style={{ opacity: sd.opacity }}
                    data-testid="atm-detail-status-badge"
                  >
                    {sd.label}{sd.timeAgo ? ` · ${sd.timeAgo}` : ""}
                  </span>
                  {showDepositChip && (
                    <span
                      className={`status-badge ${depositCap.status} text-[10px]`}
                      data-testid="atm-detail-deposit-badge"
                      title={`Deposit: ${depLabel}`}
                    >
                      ↓ Deposit {depLabel}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
          {/* Close X — thumb-reachable in the corner */}
          <button
            onClick={onClose}
            className="flex-shrink-0 w-8 h-8 rounded-full bg-black/30 border border-zinc-800/60 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors"
            data-testid="close-detail-btn"
            aria-label="Close details"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Identity block */}
        <div className="mb-1.5 flex items-center gap-2 flex-wrap">
          <h3 className="font-heading text-xl font-bold text-white leading-tight">{a.bank_name}</h3>
          {a.unit_label && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#00FF94] bg-[#00FF94]/10 px-1.5 py-0.5 rounded" data-testid="atm-unit-label">
              {a.unit_label}
            </span>
          )}
        </div>
        <p className="text-zinc-400 text-xs flex items-start gap-1.5 mb-3">
          <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" strokeWidth={1.5} />
          <span className="leading-snug">{a.address}</span>
        </p>

        {/* Trusted-Circle legend — shown when the user belongs to 1+
            active circles. Each chip pairs the ring's colour with the
            circle's name + radius so users can decode overlapping rings
            on the map at a glance. Hidden entirely otherwise. */}
        {myCircles.length > 0 && (
          <div
            className="flex flex-wrap gap-1.5 mb-3"
            data-testid="trusted-circle-legend"
          >
            {myCircles.map((c) => (
              <span
                key={c.id || c.name}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold"
                style={{
                  background: `${c.color}1A`,     // ~10% alpha of the ring colour
                  color: c.color,
                  border: `1px solid ${c.color}66`,
                }}
                title={`${c.name} · ${c.radius_km} km alert radius`}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: c.color, boxShadow: `0 0 4px ${c.color}` }}
                />
                {c.name}
                <span className="opacity-70">· {c.radius_km}km</span>
              </span>
            ))}
          </div>
        )}

        {/* Secondary actions — watch, share, admin — grouped in a subtle pill */}
        <div className="flex items-center gap-0.5 bg-black/20 border border-zinc-800/50 rounded-full p-0.5 w-fit" data-testid="atm-detail-actions">
          {user && (
            <button
              onClick={toggleWatch}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 transition-colors ${isWatching ? 'text-[#00FF94] bg-[#00FF94]/10' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}
              data-testid="watch-atm-btn"
              title={isWatching ? "Stop watching" : "Watch for alerts"}
            >
              {isWatching ? <Bell className="w-3.5 h-3.5" strokeWidth={1.75} /> : <BellOff className="w-3.5 h-3.5" strokeWidth={1.75} />}
              {isWatching ? "Watching" : "Watch"}
            </button>
          )}
          {user && (
            <button
              onClick={toggleTrustedShare}
              disabled={trustedLoading}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 transition-colors ${trustedShare ? 'text-[#00FF94] bg-[#00FF94]/10' : 'text-zinc-400 hover:text-white hover:bg-white/5'} disabled:opacity-50`}
              data-testid="trusted-share-btn"
              title={trustedShare ? "Trusted sharing ON" : "Share reports with your trusted circles"}
              aria-label="Toggle trusted circle sharing"
            >
              {trustedLoading
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.75} />
                : trustedShare
                  ? <UserCheck className="w-3.5 h-3.5" strokeWidth={1.75} />
                  : <Users2 className="w-3.5 h-3.5" strokeWidth={1.75} />}
              Share
            </button>
          )}
          {user?.is_admin && (
            <>
              <span className="w-px h-4 bg-zinc-800 mx-0.5" aria-hidden="true" />
              <button
                onClick={openAdminEdit}
                disabled={adminDeleting}
                className="p-2 rounded-full text-zinc-500 hover:text-[#00FF94] hover:bg-[#00FF94]/10 transition-colors disabled:opacity-40"
                data-testid="admin-edit-atm-btn"
                title="Edit ATM (admin)"
                aria-label="Edit ATM"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
              <button
                onClick={handleAdminDelete}
                disabled={adminDeleting}
                className="p-2 rounded-full text-zinc-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors disabled:opacity-40"
                data-testid="admin-delete-atm-btn"
                title="Delete ATM (admin)"
                aria-label="Delete ATM"
              >
                {adminDeleting
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.75} />
                  : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats Row — v2.0: rendered as chips inside a subtle inset row so the
          density reads as ONE block instead of loose text runs. */}
      <div className="px-6 pb-5 flex gap-2 flex-wrap" data-testid="atm-stats-row">
        {user?.subscription_status === "pro" ? (
          (() => {
            // Phase B: show per-cap reliability when both capabilities have
            // sufficient samples; otherwise fall back to overall (legacy).
            const rel = a.reliability;
            const w = rel?.withdraw, d = rel?.deposit;
            if (rel && w != null && d != null) {
              return (
                <div className="flex items-center gap-2 text-xs" data-testid="reliability-per-cap">
                  <TrendingUp className="w-3.5 h-3.5 text-[#00FF94]" strokeWidth={1.5} />
                  <span className="text-zinc-400">Reliability:</span>
                  <span className="text-white font-semibold">W {w}%</span>
                  <span className="text-zinc-700">·</span>
                  <span className="text-white font-semibold">D {d}%</span>
                </div>
              );
            }
            return (
              <div className="flex items-center gap-1.5 text-xs">
                <TrendingUp className="w-3.5 h-3.5 text-[#00FF94]" strokeWidth={1.5} />
                <span className="text-zinc-400">Reliability:</span>
                <span className="text-white font-semibold">{a.reliability_score || 0}%</span>
              </div>
            );
          })()
        ) : (
          <div className="flex items-center gap-1.5 text-xs">
            <Lock className="w-3.5 h-3.5 text-zinc-600" strokeWidth={1.5} />
            <span className="text-zinc-600">Reliability:</span>
            <Crown className="w-3.5 h-3.5 text-[#FFCC00]" strokeWidth={1.5} />
          </div>
        )}
        <div className="flex items-center gap-1.5 text-xs">
          <Signal className={`w-3.5 h-3.5 ${confidenceClass}`} strokeWidth={1.5} />
          <span className="text-zinc-400">Confidence:</span>
          <span className={`font-semibold capitalize ${confidenceClass}`}>{a.confidence || "low"}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <Users className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5} />
          <span className="text-zinc-400">Reports:</span>
          <span className="text-white font-semibold">{a.recent_reports || 0}</span>
        </div>
        {a.distance != null && (
          <div className="flex items-center gap-1.5 text-xs">
            <MapPin className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5} />
            <span className="text-zinc-400">Distance:</span>
            <span className="text-white font-semibold">{a.distance} km</span>
          </div>
        )}
        <div className="flex items-center gap-1.5 text-xs">
          <Clock className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5} />
          <span className="text-zinc-400">Line:</span>
          <span className="text-white font-semibold">{LINE_LABELS[a.line_status] || "Unknown"}</span>
        </div>
      </div>

      {/* Peek-only CTA — one-tap "Report ATM Status" that expands the sheet
          and auto-scrolls to the Submit Report section. Hidden entirely
          when the sheet is already in `expanded` mode via .sheet-peek-only. */}
      <div className="px-6 pb-4 sheet-peek-only">
        <button
          type="button"
          onClick={expandAndScrollToReport}
          className="sheet-report-cta"
          data-testid="expand-to-report-btn"
        >
          <Send className="w-4 h-4" strokeWidth={2.25} />
          Report ATM Status
        </button>
        <p className="text-[10px] text-zinc-500 text-center mt-2">
          Tap the handle above to expand · swipe down to close
        </p>
      </div>

      {/* Expanded-only wrapper — collapses to zero height in peek mode.
          Contains capability rows, the full Submit Report form, and the
          Recent Reports list. */}
      <div className="sheet-expanded-only" data-testid="atm-detail-expanded-content">

      {/* Per-capability status — only shown when at least one capability has data
          AND we have both capabilities (or only deposit). Pure-withdraw ATMs use
          the existing single status badge in the header to avoid noise. */}
      {(() => {
        const caps = a.capabilities || {};
        const w = caps.withdraw, d = caps.deposit;
        // Hide unknown rows. Show the panel only if deposit data exists OR both
        // capabilities have data — withdraw-only is the legacy display.
        if (!d) return null;
        return (
          <div className="px-6 pb-4 -mt-2" data-testid="capability-rows">
            <div className="grid grid-cols-2 gap-2">
              {w && (
                <CapabilityRow label="Withdraw" cap={w} testId="cap-row-withdraw" />
              )}
              <CapabilityRow label="Deposit" cap={d} testId="cap-row-deposit" />
            </div>
          </div>
        );
      })()}

      {/* Report Buttons */}
      <div className="px-6 pb-4" ref={submitReportRef} data-testid="submit-report-section">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-zinc-500 font-semibold uppercase tracking-wider">Submit Report</p>
          {user && (
            <span className={`text-[10px] font-semibold ${cooldown.can_report ? 'text-zinc-500' : 'text-[#FF3B30]'}`} data-testid="cooldown-indicator">
              {cooldown.can_report
                ? `${cooldown.reports_remaining} report${cooldown.reports_remaining === 1 ? "" : "s"} left this hour`
                : `Cooldown — try again later`}
            </span>
          )}
        </div>

        {/* Step 0: Transaction Type — Phase A. Picking deposit auto-clears the
            "No Cash" status because deposits can't run out of cash. */}
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5 font-semibold">Transaction</p>
        <div className="grid grid-cols-2 gap-2 mb-3" data-testid="transaction-type-toggle">
          <button
            type="button"
            onClick={() => setTransactionType("withdraw")}
            disabled={submitting || !user || !cooldown.can_report}
            className={`report-btn line-btn flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed ${transactionType === "withdraw" ? "ring-2 ring-[#00FF94] ring-offset-1 ring-offset-[#09090B] !text-[#00FF94]" : ""}`}
            data-testid="txn-withdraw-btn"
          >
            {/* Withdraw = cash flowing OUT of the ATM towards the user.
                `HandCoins` is a hand receiving coins — the most literally
                readable icon for withdrawal in Lucide. */}
            <HandCoins className="w-4 h-4" strokeWidth={1.75} />
            Withdraw
          </button>
          <button
            type="button"
            onClick={() => {
              setTransactionType("deposit");
              if (selectedStatus === "out_of_cash") setSelectedStatus(null);
            }}
            disabled={submitting || !user || !cooldown.can_report}
            className={`report-btn line-btn flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed ${transactionType === "deposit" ? "ring-2 ring-[#00FF94] ring-offset-1 ring-offset-[#09090B] !text-[#00FF94]" : ""}`}
            data-testid="txn-deposit-btn"
          >
            {/* Deposit = cash flowing INTO the ATM. `PiggyBank` reads
                universally as "put money away / save" — clear, physical,
                and immediately distinct from HandCoins. */}
            <PiggyBank className="w-4 h-4" strokeWidth={1.75} />
            Deposit
          </button>
        </div>

        {/* Step 1: ATM Status */}
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5 font-semibold">ATM Status</p>
        <div className={`grid ${transactionType === "deposit" ? "grid-cols-2" : "grid-cols-3"} gap-2 mb-3`} data-testid="status-report-buttons">
          <button
            onClick={() => setSelectedStatus("working")}
            disabled={submitting || !user || !cooldown.can_report}
            className={`report-btn working-btn disabled:opacity-40 disabled:cursor-not-allowed ${selectedStatus === "working" ? "ring-2 ring-[#00FF94] ring-offset-1 ring-offset-[#09090B]" : ""}`}
            data-testid="report-working-btn"
          >
            <CheckCircle className="w-5 h-5" strokeWidth={1.5} />
            Working
          </button>
          {transactionType !== "deposit" && (
            <button
              onClick={() => setSelectedStatus("out_of_cash")}
              disabled={submitting || !user || !cooldown.can_report}
              className={`report-btn out-of-cash-btn disabled:opacity-40 disabled:cursor-not-allowed ${selectedStatus === "out_of_cash" ? "ring-2 ring-[#FFCC00] ring-offset-1 ring-offset-[#09090B]" : ""}`}
              data-testid="report-no-cash-btn"
            >
              <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
              No Cash
            </button>
          )}
          <button
            onClick={() => setSelectedStatus("out_of_service")}
            disabled={submitting || !user || !cooldown.can_report}
            className={`report-btn out-of-service-btn disabled:opacity-40 disabled:cursor-not-allowed ${selectedStatus === "out_of_service" ? "ring-2 ring-[#FF3B30] ring-offset-1 ring-offset-[#09090B]" : ""}`}
            data-testid="report-offline-btn"
          >
            <XCircle className="w-5 h-5" strokeWidth={1.5} />
            Offline
          </button>
        </div>

        {/* Step 2: Line Length */}
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5 font-semibold">Line Length</p>
        <div className="grid grid-cols-3 gap-2 mb-3" data-testid="line-report-buttons">
          <button
            onClick={() => setSelectedLine("no_line")}
            disabled={submitting || !user || !cooldown.can_report}
            className={`report-btn line-btn disabled:opacity-40 disabled:cursor-not-allowed ${selectedLine === "no_line" ? "ring-2 ring-white/50 ring-offset-1 ring-offset-[#09090B]" : ""}`}
            data-testid="report-no-line-btn"
          >
            <Minus className="w-5 h-5" strokeWidth={1.5} />
            No Line
          </button>
          <button
            onClick={() => setSelectedLine("short_line")}
            disabled={submitting || !user || !cooldown.can_report}
            className={`report-btn line-btn disabled:opacity-40 disabled:cursor-not-allowed ${selectedLine === "short_line" ? "ring-2 ring-white/50 ring-offset-1 ring-offset-[#09090B]" : ""}`}
            data-testid="report-short-line-btn"
          >
            <Users className="w-5 h-5" strokeWidth={1.5} />
            Short Line
          </button>
          <button
            onClick={() => setSelectedLine("long_line")}
            disabled={submitting || !user || !cooldown.can_report}
            className={`report-btn line-btn disabled:opacity-40 disabled:cursor-not-allowed ${selectedLine === "long_line" ? "ring-2 ring-white/50 ring-offset-1 ring-offset-[#09090B]" : ""}`}
            data-testid="report-long-line-btn"
          >
            <Users className="w-5 h-5" strokeWidth={1.5} />
            Long Line
          </button>
        </div>

        {/* Step 3: Optional comment (Phase A — 100 char public note) */}
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5 font-semibold flex items-center justify-between">
          <span>Comment <span className="text-zinc-600 normal-case font-normal">(optional)</span></span>
          <span className={`text-[10px] ${comment.length > 90 ? 'text-[#FFCC00]' : 'text-zinc-600'}`} data-testid="comment-counter">{comment.length}/100</span>
        </p>
        <input
          type="text"
          maxLength={100}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={submitting || !user || !cooldown.can_report}
          placeholder="e.g. JMD only · ABM down · long line"
          className="w-full mb-3 px-3 h-9 text-sm rounded-lg bg-zinc-900 border border-zinc-800 text-white placeholder:text-zinc-600 focus:border-[#00FF94]/50 focus:outline-none disabled:opacity-40"
          data-testid="report-comment-input"
        />

        {/* Submit Button */}
        {user && cooldown.can_report && (selectedStatus || selectedLine) && (
          <button
            onClick={submitReport}
            disabled={submitting || !selectedStatus || !selectedLine}
            className="w-full py-2.5 rounded-lg text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-[#00FF94] text-black hover:bg-[#00FF94]/90"
            data-testid="submit-report-btn"
          >
            {submitting ? "Submitting..." : `Submit Report${selectedStatus && selectedLine ? "" : " (select both)"}`}
          </button>
        )}
        {!user && (
          <p className="text-zinc-600 text-xs text-center mt-2">Sign in to submit reports</p>
        )}
        {user && !cooldown.can_report && cooldown.next_available && (
          <p className="text-[#FF3B30] text-xs text-center mt-2" data-testid="cooldown-message">
            You've reached the limit. Next report available at {formatJamaicaTime(cooldown.next_available)}
          </p>
        )}
      </div>

      {/* Recent Reports */}
      {reports.length > 0 && (
        <div className="px-6 pb-6">
          <p className="text-xs text-zinc-500 mb-3 font-semibold uppercase tracking-wider">Recent Reports</p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {reports.slice(0, 5).map((r) => (
              <div key={r.id} className="bg-zinc-900/50 rounded-lg p-2.5 border border-zinc-800/50" data-testid={`recent-report-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: STATUS_COLORS[r.status] || STATUS_COLORS.unknown }}
                    />
                    <span className="text-xs text-zinc-300">{STATUS_LABELS[r.status] || "Unknown"}</span>
                    {r.transaction_type && (
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">
                        {r.transaction_type === "deposit" ? "Deposit" : "Withdraw"}
                      </span>
                    )}
                    {r.line_status && r.line_status !== "unknown" && (
                      <span className="text-[10px] text-zinc-500">| {LINE_LABELS[r.line_status]}</span>
                    )}
                  </div>
                  <span className="text-[10px] text-zinc-600 flex-shrink-0">
                    {formatTimeAgo(r.timestamp)}
                  </span>
                </div>
                {r.comment && (
                  <p className="text-[11px] text-zinc-400 mt-1.5 italic break-words" data-testid={`recent-report-comment-${r.id}`}>
                    "{r.comment}"
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      </div>
      {/* /sheet-expanded-only */}

      <HelpButton screen="atm-detail" tips={ATM_DETAIL_HELP} />

      {/* Admin Edit Dialog — only mounted when opened by an admin. */}
      {user?.is_admin && (
        <Dialog open={adminEditOpen} onOpenChange={setAdminEditOpen}>
          <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-md" data-testid="admin-edit-atm-dialog">
            <DialogHeader>
              <DialogTitle>Edit ATM</DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs">
                Changes apply to all users immediately.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <BankSelect
                value={adminEditForm.bank_name}
                onChange={(v) => setAdminEditForm({ ...adminEditForm, bank_name: v })}
                testId="admin-edit-bank-name"
                className="h-9"
              />
              <Input
                value={adminEditForm.address_street}
                onChange={(e) => setAdminEditForm({ ...adminEditForm, address_street: e.target.value })}
                placeholder="Street"
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="admin-edit-street"
              />
              <Input
                value={adminEditForm.address_area}
                onChange={(e) => setAdminEditForm({ ...adminEditForm, address_area: e.target.value })}
                placeholder="Area / Town"
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="admin-edit-area"
              />
              <Input
                value={adminEditForm.address_parish}
                onChange={(e) => setAdminEditForm({ ...adminEditForm, address_parish: e.target.value })}
                placeholder="Parish"
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="admin-edit-parish"
              />
              <Input
                value={adminEditForm.unit_label}
                onChange={(e) => setAdminEditForm({ ...adminEditForm, unit_label: e.target.value })}
                placeholder="Unit Label (e.g. ATM #2) — optional"
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="admin-edit-unit-label"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={adminEditForm.latitude}
                  onChange={(e) => setAdminEditForm({ ...adminEditForm, latitude: e.target.value })}
                  placeholder="Latitude"
                  inputMode="decimal"
                  className="bg-zinc-900 border-zinc-800 text-white"
                  data-testid="admin-edit-latitude"
                />
                <Input
                  value={adminEditForm.longitude}
                  onChange={(e) => setAdminEditForm({ ...adminEditForm, longitude: e.target.value })}
                  placeholder="Longitude"
                  inputMode="decimal"
                  className="bg-zinc-900 border-zinc-800 text-white"
                  data-testid="admin-edit-longitude"
                />
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="ghost"
                onClick={() => setAdminEditOpen(false)}
                disabled={adminSaving}
                className="text-zinc-400 hover:text-white"
                data-testid="admin-edit-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={submitAdminEdit}
                disabled={adminSaving || !adminEditForm.bank_name}
                className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90"
                data-testid="admin-edit-save-btn"
              >
                {adminSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function formatTimeAgo(timestamp) {
  const now = new Date();
  const then = new Date(timestamp);
  const diff = Math.floor((now - then) / 1000 / 60);
  if (diff < 1) return "Just now";
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
}

function CapabilityRow({ label, cap, testId }) {
  const status = cap?.status || "unknown";
  const color = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  const isStale = cap?.confidence === "stale" || cap?.confidence === "none";
  return (
    <div
      className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-zinc-900/50 border border-zinc-800/50"
      style={{ opacity: isStale ? 0.7 : 1 }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">{label}</span>
      </div>
      <span className="text-xs text-zinc-200 font-semibold truncate">{STATUS_LABELS[status] || "Unknown"}</span>
    </div>
  );
}

function SiblingPill({ active, index, label, status, loading, onClick, testId }) {
  const color = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  const display = label || (index ? `ATM #${index}` : "ATM");
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={active || loading}
      className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold whitespace-nowrap transition-all border ${
        active
          ? "bg-[#00FF94]/15 text-[#00FF94] border-[#00FF94]/40 cursor-default"
          : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-zinc-600 hover:text-white"
      } ${loading ? "opacity-50" : ""}`}
      data-testid={testId}
    >
      <span
        className="inline-block w-2 h-2 rounded-full flex-shrink-0"
        style={{ background: color }}
      />
      {display}
      {active && <span className="text-[9px] uppercase tracking-wider opacity-80">Viewing</span>}
    </button>
  );
}
